import { CdkDragDrop, CdkDragMove } from "@angular/cdk/drag-drop";
import { Injectable } from "@angular/core";
import { ESigningOrderType } from "core/dto";
import { IDisposable } from "core/dynamic-form";
import { pairBehaviorSubjects } from "core/rx-js";
import { LoaderService } from "core/services";
import { UserService } from "projects/designer/src/app/global-services/user.service";
import { DsgDeepTranslationKey } from "projects/designer/src/app/pipes/translate.pipe";
import { BehaviorSubject, Subject, filter, map, take, takeUntil, tap } from "rxjs";
import { IActivity } from "../../../dto-map/i-activity";
import { RemoteActivityService } from "../../../remotes/remote-activity.service";
import { RemoteSplugParametersConfigurationService } from "../../../remotes/remote-splug-parameters-configuration.service";
import { SignProcessWizardService } from "../../../sign-process-wizard.service";
import { ActivityItemComponent } from "./activity-item/activity-item.component";




@Injectable()
export class ActivityCollectionService {

  private readonly _destroy = new Subject<void>();
  private readonly _disposables: IDisposable[] = [];

  readonly activitiesSubject = this._remoteActivityService.localCollectionStore.stateSubject;
  readonly showNewActivityButtonSubject = new BehaviorSubject<boolean>(false);
  readonly moveSubject = new Subject<CdkDragMove>()
  readonly dragModeSubject = new BehaviorSubject<boolean>(false);

  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  get processId() {
    return this.processIdSubject.value;
  }
  readonly activityDragComponentsSubject = new Subject<ActivityItemComponent[]>

  readonly activitiesGroupedObs = this.activitiesSubject.pipe(
    filter(Boolean),
    map((acitvities) => {
      const acitvitiesGrouped = acitvities.reduce<{ [orderIndex: number]: IActivity[] }>((acitvitiesGrouped, activity) => {
        if (!acitvitiesGrouped[activity.orderIndex]) {
          acitvitiesGrouped[activity.orderIndex] = [];
        }

        acitvitiesGrouped[activity.orderIndex].push(activity)
        return acitvitiesGrouped;
      },
        {}
      )

      const acitvitiesSorted = Object.entries(acitvitiesGrouped)
        //todo add in group index for ui
        .map(([groupIdx, acitvities]) => [groupIdx, acitvities.sort(({ orderIndex: first }, { orderIndex: second }) => first - second)] as const)
        .sort(([groupIdxFirst], [groupIdxSecond]) => Number(groupIdxFirst) - Number(groupIdxSecond))

      return acitvitiesSorted;

    })
  )

  readonly addingActivitySubject = new BehaviorSubject<boolean>(false);
  readonly dropSubject = new Subject<CdkDragDrop<any>>

  readonly processSigningOrderSubject = this._signProcessWizardService.processSigningOrderSubject;
  get #processSigningOrder() {
    return this.processSigningOrderSubject.value
  }

  get user() {
    return this._userService.userSubject.value;
  }
  constructor(
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _remoteActivityService: RemoteActivityService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _userService: UserService,
    private readonly _remoteSplugParametersConfigurationService: RemoteSplugParametersConfigurationService,

  ) {


    this.dropSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((drop) => {

        this.moveSubject.next(undefined);

        if (this.#processSigningOrder === ESigningOrderType.custom) {
          this._reorderActivities(drop);
          return;
        }
        else {
          this._reorderFlat(drop)
        }

      })

    const disposable = pairBehaviorSubjects(
      this.activitiesSubject,
      this._signProcessWizardService.activitiesSubject
    )

    this._disposables.push(disposable);


  }

  private _reorderFlat(drop: CdkDragDrop<any>) {
    const {
      container,
      previousContainer
    } = drop;

    if (container === previousContainer) {
      return;
    }

    const dragItem = drop.item;
    const activityComp: ActivityItemComponent = dragItem.data;
    const currentGroupIdx = container.data.groupIdx;
    const prevGroupIdx = previousContainer.data.groupIdx;

    const droppedActivityId = activityComp.activityId;
    const newActivities = [...this.activitiesSubject.value];

    const activity = newActivities.find(({ id }) => id === droppedActivityId);
    if (!activity) {
      return;
    }

    newActivities
      .filter(({ orderIndex }) => orderIndex >= prevGroupIdx)
      .forEach((activity) => activity.orderIndex = activity.orderIndex - 1)


    newActivities
      .filter(({ orderIndex }) => orderIndex >= currentGroupIdx)
      .forEach((activity) => activity.orderIndex = activity.orderIndex + 1)

    activity.orderIndex = currentGroupIdx;

    this._remoteActivityService.reorderShallow(
      newActivities.map((activity) => ({ key: activity.id as string, orderIdx: activity.orderIndex }))
    ).pipe(take(1)).subscribe();

    this.activitiesSubject.next(newActivities)

  }

  private _reorderActivities(drop: CdkDragDrop<any>) {

    const newActivities = [...this.activitiesSubject.value];

    const {
      container,
      previousContainer,
      previousIndex,
      currentIndex
    } = drop;

    if (container === previousContainer) {
      return;
    }

    const prevGroupIdx: number = previousContainer.data.groupIdx;
    let currentGroupIdx = container.data.groupIdx;

    const dragItem = drop.item;
    const activityComp: ActivityItemComponent = dragItem.data;

    const fromGroupIsNowEmpty = newActivities.filter(({ orderIndex }) => orderIndex === prevGroupIdx).length === 1;

    if (fromGroupIsNowEmpty) {
      newActivities
        .filter(({ orderIndex }) => orderIndex > prevGroupIdx)
        .forEach((activity) => activity.orderIndex = activity.orderIndex - 1)

      if (currentGroupIdx > prevGroupIdx) {
        currentGroupIdx--

      }
    }
    else {

      newActivities
        .filter(({ orderIndex, inGroupIndex }) => inGroupIndex && orderIndex === prevGroupIdx && inGroupIndex > previousIndex)
        .forEach(activity => {
          activity.inGroupIndex = (activity.inGroupIndex || 1) - 1
        })

    }

    const droppedActivityUuid = activityComp?.activity?.id;

    if (!droppedActivityUuid) {
      return
    }

    const droppedActivity = newActivities.find(({ id }) => id === droppedActivityUuid);

    if (!droppedActivity) {
      return;
    }

    if (Number.isInteger(currentGroupIdx)) {

      droppedActivity.orderIndex = currentGroupIdx;

      newActivities
        .filter(({ orderIndex, inGroupIndex }) => inGroupIndex && orderIndex === currentGroupIdx && inGroupIndex >= currentIndex)
        .forEach((activity) => {
          activity.inGroupIndex = (activity.inGroupIndex || 0) + 1
        })

      droppedActivity.inGroupIndex = currentIndex;
      this._remoteActivityService.reorderShallow(
        newActivities.map((activity) => ({ key: activity.id as string, orderIdx: activity.orderIndex }))
      )
        .pipe(take(1))
        .subscribe();

      this.activitiesSubject.next(newActivities);
      return;
    }

    newActivities
      .filter(({ orderIndex }) => orderIndex > currentGroupIdx)
      .forEach((activity) => activity.orderIndex = activity.orderIndex + 1)

    droppedActivity.orderIndex = currentGroupIdx + 0.5;
    droppedActivity.inGroupIndex = 0;

    this._remoteActivityService.reorderShallow(
      newActivities.map((activity) => ({ key: activity.id as string, orderIdx: activity.orderIndex }))
    )
      .pipe(take(1))
      .subscribe();
    this.activitiesSubject.next(newActivities)

  }


  reorder(params: { previousIndex: number, currentIndex: number }) {
    const { currentIndex, previousIndex } = params;

    if (previousIndex === currentIndex) {
      return;
    }

    const acitvities = [...this.activitiesSubject.value];
    const [movedSigner] = acitvities.splice(previousIndex, 1);
    acitvities.splice(currentIndex, 0, movedSigner)

    this.activitiesSubject.next(acitvities);
  }



  addNewActivity() {
    const prevActivities = [...this.activitiesSubject.value];
    this.addingActivitySubject.next(true);


    const maxOrderIdx = Math.max(-1, ...prevActivities.map(({ orderIndex }) => orderIndex))

    const orderIndex = ESigningOrderType.paralel === this.#processSigningOrder ? 0 : (maxOrderIdx + 1);


    const lastGroupIndex = prevActivities.find(({ orderIndex: searchOrderIndex }) => searchOrderIndex === orderIndex)?.inGroupIndex
    const inGroupIndex = (this.#processSigningOrder === ESigningOrderType.paralel || lastGroupIndex === undefined) ? 0 : 1

    const newActivity: IActivity = {
      processId: this.processId,
      orderIndex,
      inGroupIndex
    };

    this._remoteActivityService.insert(newActivity, 'reloadChangedRemote')
      .pipe(take(1))
      .subscribe()
  }

  addSelf() {

    const id = this._loaderService.show({ key: 'add-self' })

    const prevActivities = [...this.activitiesSubject.value];
    this.addingActivitySubject.next(true);

    const orderIndex = this.#processSigningOrder === ESigningOrderType.paralel ? 0 : (Math.max(-1, ...prevActivities.map((act) => act.orderIndex)) + 1);
    const newActivity: IActivity = {
      processId: this.processId,
      orderIndex,
      inGroupIndex: prevActivities.find(({ orderIndex: searchOrderIndex }) => searchOrderIndex === orderIndex)?.inGroupIndex === undefined ? 0 : 1,
      actor: {
        email: this.user.email,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        mobile: this.user.mobile,
        hue: null
      },

    };


    this._remoteActivityService.insert(newActivity, 'reloadChangedRemote')
      .pipe(
        take(1),
        tap(activityId => {

          this._remoteSplugParametersConfigurationService.loadByActivity(activityId)
            .pipe(take(1))
            .subscribe()
        })
      )
      .subscribe()
  }

  remove(uuid: string) {

    this._remoteActivityService.remove(uuid, 'reloadChanged').pipe(
      take(1)
    ).subscribe();
  }

  ngOnDestroy() {

    this._destroy.next();
    this._destroy.complete();

    this.moveSubject.complete();
    this.showNewActivityButtonSubject.complete();
    this.activityDragComponentsSubject.complete();
    this.addingActivitySubject.complete();
    this.dropSubject.complete();

    this._disposables.forEach(({ dispose }) => dispose());
  }

  private _getHueByIdx(idx: number) {

    const hues = [
      0,
      180,
      90,
      270,
      45,
      225,
      135,
      215,
    ]

    return hues[idx % hues.length];
  }
}
